import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        await register(email, password);
        toast.success('Registration successful! Please login.');
        setIsRegistering(false);
      } else {
        await login(email, password);
        toast.success('Login successful!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
      {/* Left Panel - Login Form */}
      <div className="flex items-center justify-center p-8 bg-[#0B0C10]">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_mailcraft-13/artifacts/hs5d8bkj_dommo_logo.png" 
              alt="Dommo Logo" 
              className="h-20 w-20 mx-auto mb-4 object-contain"
            />
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Dommo Swiss Mailer
            </h1>
            <p className="text-[#9CA3AF]">
              {isRegistering ? 'Create your account' : 'Sign in to your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#F3F4F6]">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF]" size={20} />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#13151A] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#F3F4F6]">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF]" size={20} />
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#13151A] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
            >
              {loading ? 'Processing...' : isRegistering ? 'Register' : 'Sign In'}
            </Button>
          </form>

          <div className="text-center">
            <button
              onClick={() => setIsRegistering(!isRegistering)}
              data-testid="toggle-auth-mode"
              className="text-[#183871] hover:text-[#1F4BA1] text-sm"
            >
              {isRegistering ? 'Already have an account? Sign in' : "Don't have an account? Register"}
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel - Background Image */}
      <div className="hidden lg:flex flex-col justify-center p-12 bg-[#13151A] relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20" 
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761767380566-7a86c3e653a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwZ3JhZGllbnQlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc2NDY1ODA0Nnww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 space-y-6">
          <h2 className="text-5xl font-bold text-[#F3F4F6] tracking-tight">
            Build Beautiful
            <br />
            Email Campaigns
          </h2>
          <p className="text-xl text-[#9CA3AF] leading-relaxed">
            Professional email marketing platform with drag-and-drop builder,
            multi-domain support, and SendGrid integration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
